import React, { useState } from 'react';
import { orderBy, map } from 'lodash';
import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { TestId } from '@components/test-ids';
import { Grid } from '@mui/material';
import { Theme } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { TripTypes } from '@model/common';
import { NavDestination } from '@model/content';
import { TourDestinationDetails } from '@model/tours';
import { useI18NextContext } from '@components/hooks';
import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import { TourProductTileComponent, TourProductTileComponentProps } from '@components/product/tour-product-tile';
import { TourDestinationSort } from './TourDestinationSort';

export interface TourDestinationGridProps {
  theme?: Theme;
  destination: NavDestination;
  tripType: TripTypes;
  packages: Array<TourDestinationDetails>;
  searchToken?: string;
}

interface SortOption {
  label: string;
  fn: (packages: Array<TourDestinationDetails>) => Array<TourDestinationDetails>;
}

const Container = styled.div({});
const HeaderContainer = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
  [mq.small]: {
    flexDirection: 'row'
  }
});

export const TourDestinationGridComponent = ({ theme, destination, tripType, packages }: TourDestinationGridProps) => {
  const [sortIndex, setSortIndex] = useState<number>(0);
  const t: any = useI18NextContext();

  const { name: placeName } = destination;

  const sortOptions: Array<SortOption> = [
    { label: `${t('filters__sort_by--popularity')}: ${t('sort__high-low')}`, fn: (packages) => packages },
    {
      label: `${t('filters__sort_by--popularity')}: ${t('sort__low-high')}`,
      fn: (packages) => packages.slice().reverse()
    },
    {
      label: `${t('filters__sort_by--price')}: ${t('sort__high-low')}`,
      fn: (packages) => orderBy(packages, 'price.amount', 'desc')
    },
    {
      label: `${t('filters__sort_by--price')}: ${t('sort__low-high')}`,
      fn: (packages) => orderBy(packages, 'price.amount', 'asc')
    },
    {
      label: `${t('filters__sort_by--tour-length')}: ${t('sort__long-short')}`,
      fn: (packages) => orderBy(packages, 'days', 'desc')
    },
    {
      label: `${t('filters__sort_by--tour-length')}: ${t('sort__short-long')}`,
      fn: (packages) => orderBy(packages, 'days', 'asc')
    }
  ];

  const onSortChange = (filter) => {
    const selected = filter.values.find(({ selected }) => selected);
    setSortIndex(selected.value);
  };

  /* *** RENDERERS *** */
  const renderSearchResults = () =>
    sortOptions[sortIndex].fn(packages).map((tour: TourDestinationDetails) => {
      const { name, excursionsCount, mealsCount, pace, path, price, subTitle, gallery } = tour;
      const featuredImage = gallery[0];

      const tileProps: TourProductTileComponentProps = {
        path: path,
        tripType,
        testId: TestId.tourResultsGrid.resultTile,
        tourName: name || '',
        price,
        discount: price?.discount,
        featuredImage,
        gallery,
        country: placeName,
        subTitle,
        mealsCount,
        excursionsCount,
        pace,
        pageLink: true
      };
      return (
        <Grid item xs={12} md={6} lg={4} key={tour.path}>
          <TourProductTileComponent {...tileProps} />
        </Grid>
      );
    });

  return (
    <Container data-testid={TestId.tourResultsGrid.main}>
      <HeaderContainer>
        <SectionTitle
          size={SectionTitleSizes.LARGE}
          heading={t('tours-search__page-title--tours-in', { place: placeName })}
        />
        <TourDestinationSort onChange={onSortChange} sortBy={sortIndex} sortLabels={map(sortOptions, 'label')} />
      </HeaderContainer>
      <Grid container spacing={theme!.custom.gridSpacing.default}>
        {renderSearchResults()}
      </Grid>
    </Container>
  );
};

export const TourDestinationGrid = withTheme(TourDestinationGridComponent);
